<template>
  <div v-if="selectedRelease" class="menu-position">
    <div class="tools-strip">
      <button
        class="tool-btn"
        :class="{ 'tool-btn--active': extentsPanelOpen }"
        title="Change Extent"
        @click="toggleExtentsPanel"
      >
        <fa :icon="['fas', 'globe-americas']" size="sm" />
      </button>

      <button
        id="base-maps"
        class="tool-btn"
        :class="{ 'tool-btn--active': baseMapsPanelOpen }"
        title="Switch Base Map"
        @click="toggleBaseMapsPanel"
      >
        <fa :icon="['fas', 'map']" size="sm" />
      </button>

      <button
        class="tool-btn"
        :class="{ 'tool-btn--active': lidarToggle, 'tool-btn--warn': !lidarMinZoomAchieved }"
        title="2D Lidar"
        @click="toggleLidar"
      >
        <fa :icon="['fas', lidarMinZoomAchieved ? 'layer-group' : 'exclamation-triangle']" size="sm" />
      </button>

      <button
        class="tool-btn"
        :class="{ 'tool-btn--active': measuringToolOn }"
        title="Measuring Tool"
        @click="toggleMeasure"
      >
        <fa :icon="['fas', 'ruler']" size="sm" />
      </button>
    </div>

    <div class="tools-popup-area">
      <div class="tools-popup" v-show="extentsPanelOpen">
        <Generic_CollapsedExtents
          :map="map"
          :selectedExtent="selectedExtent"
          :userOrg="user ? user.org : null"
          @extentChanged="onExtentChanged"
        />
      </div>

      <div class="tools-popup" v-show="baseMapsPanelOpen">
        <MMP_CollapsedBaseMap
          :currentBaseMap="currentBaseMap"
          :expanded="baseMapsPanelOpen"
          :map="map"
          :isAnsiComponent="false"
          @toggleBaseLayer="$emit('toggleBaseLayer', $event)"
        />
      </div>

      <div class="lidar-popup" v-show="lidarToggle">
        <Generic_LidarTiles
          :showLidarPanel="lidarToggle"
          :lidarMinZoomAchieved="lidarMinZoomAchieved"
          :distinct_lidar="distinct_lidar"
          :lidarIsLoading="lidarIsLoading"
          :map="map"
          :selectedExtent="selectedExtent"
          :zoomLevel="zoomLevel"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.menu-position {
  position: relative;
}

.tools-strip {
  display: flex;
  flex-direction: row;
  background: #063c6d;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
  overflow: hidden;
}

.tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.125rem;
  height: 2.5rem;
  background: none;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  flex-shrink: 0;
}

.tool-btn:last-child {
  border-right: none;
}

.tool-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.tool-btn--active {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.tool-btn--warn {
  color: #ffc107;
}

/* Popup panel area — attached directly under the strip so it reads as one unit */
.tools-popup-area {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  z-index: 200;
}

.tools-popup {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: #063c6d;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(6, 60, 109, 0.45);
  overflow: hidden;
  transform-origin: top center;
  animation: toolsDropDown 0.18s ease-out both;
}

/* Gentle slide-down reveal — plays each time the panel is shown (v-show flip) */
@keyframes toolsDropDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Vertical list of compact base map cards inside the dark popup (matches MMP_Widgets) */
.tools-popup ::v-deep .basemap-panel {
  width: 200px !important;
  min-width: 200px !important;
  padding: 8px 10px 10px !important;
}
.tools-popup ::v-deep .basemap-row {
  flex-direction: column !important;
  flex-wrap: nowrap !important;
  gap: 5px !important;
  overflow: visible !important;
  max-height: none !important;
}
.tools-popup ::v-deep .basemap-card {
  flex-direction: row !important;
  align-items: center !important;
  gap: 8px !important;
  width: 100% !important;
  padding: 5px 7px !important;
  border-radius: 6px !important;
}
.tools-popup ::v-deep .basemap-img {
  width: 48px !important;
  height: 34px !important;
  object-fit: contain !important;
  border-radius: 3px !important;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.06);
}
.tools-popup ::v-deep .basemap-name {
  text-align: left !important;
  font-size: 0.75rem !important;
  color: rgba(255, 255, 255, 0.9) !important;
  white-space: normal !important;
  word-break: break-word !important;
  line-height: 1.25 !important;
}
.tools-popup ::v-deep .basemap-label {
  color: rgba(255, 255, 255, 0.45) !important;
  font-size: 0.6rem !important;
}
</style>

<script>
import CircleStyle from 'ol/style/Circle'
import { Draw } from 'ol/interaction'
import Fill from 'ol/style/Fill'
import LineString from 'ol/geom/LineString'
import Polygon from 'ol/geom/Polygon'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import {getLength, getArea, getDistance} from 'ol/sphere'
import {
  get as projGet,
  transform
} from 'ol/proj'
import Generic_CollapsedExtents from './Generic_CollapsedExtents.vue'
import Generic_LidarTiles from './Generic_LidarTiles.vue'
import MMP_CollapsedBaseMap from '../shared/MMP_CollapsedBaseMap.vue'
export default {
  name: 'Generic_Widgets',
  components: {
    Generic_CollapsedExtents,
    Generic_LidarTiles,
    MMP_CollapsedBaseMap
  },
  props: [
    'map',
    'currentBaseMap',
    'selectedExtent',
    'user',
    'lidarMinZoomAchieved',
    'selectedRelease',
    'distinct_lidar',
    'lidarIsLoading',
    'zoomLevel'
  ],
  data() {
    return {
      extentsPanelOpen: false,
      baseMapsPanelOpen: false,
      measuringToolOn: false,
      draw: null,
      measurement: ['0', 'm'],
      currentExtent: null,
      lidarToggle: false,
      faIcon: 'exclamation-triangle'
    }
  },
  methods: {
    toggleExtentsPanel() {
      this.extentsPanelOpen = !this.extentsPanelOpen
      if (this.extentsPanelOpen) {
        this.baseMapsPanelOpen = false
        if (this.lidarToggle) this.closeLidarPanel()
        if (this.measuringToolOn) this.toggleMeasure()
      }
    },
    onExtentChanged(extent) {
      this.extentsPanelOpen = false
      this.$emit('extentChanged', extent)
    },
    toggleBaseMapsPanel() {
      this.baseMapsPanelOpen = !this.baseMapsPanelOpen
      if (this.baseMapsPanelOpen) {
        this.extentsPanelOpen = false
        if (this.lidarToggle) this.closeLidarPanel()
        if (this.measuringToolOn) this.toggleMeasure()
      }
    },
    toggleLidar() {
      this.lidarToggle = !this.lidarToggle
      if (this.lidarToggle) {
        if (this.extentsPanelOpen) this.extentsPanelOpen = false
        this.baseMapsPanelOpen = false
        if (this.measuringToolOn) this.toggleMeasure()
      }
      // Always notify the parent, even below the zoom threshold — Generic.vue's
      // toggleLidarPanel()/lidarDownload() is what actually sets lidarIsLoading
      // back to false and shows the "zoom in further" message. Gating this on
      // lidarMinZoomAchieved left the parent's showLidarPanel/lidarIsLoading
      // state stale whenever the panel was opened while zoomed out, so the
      // spinner (or a leftover Lidar layer) could get stuck indefinitely.
      this.$emit('toggleLidarPanel')
    },
    closeLidarPanel() {
      // Used when another widget (e.g. Change Extent) needs to force the lidar
      // popup shut. Must go through the same emit as toggleLidar() so
      // Generic.vue's showLidarPanel/clearLidar() cleanup actually runs —
      // flipping lidarToggle alone only hides the popup, it leaves the lidar
      // map layers and distinct_lidar state stale.
      this.lidarToggle = false
      this.$emit('toggleLidarPanel')
    },
    toggleMeasure() {
      this.measuringToolOn = !this.measuringToolOn
      this.$emit('toggleMeasuringTool')
      if (this.measuringToolOn) {
        if (this.extentsPanelOpen) this.extentsPanelOpen = false
        this.baseMapsPanelOpen = false
        if (this.lidarToggle) this.closeLidarPanel()
        this.addInteraction()
        this.$emit('measurement', this.measurement)
      } else {
        if (this.map) {
          this.map.removeInteraction(this.draw)
        }
        this.measurement = ['0', 'm']
      }
    },
    addInteraction() {
      var sketch
      var source = new VectorSource()
      var vector = new VectorLayer({
        source: source,
        zIndex: 200,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          }),
          stroke: new Stroke({
            color: '#ffcc33',
            width: 2
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: '#ffcc33'
            })
          })
        })
      })
      var type = 'LineString'
      this.draw = new Draw({
        source: source,
        type: type,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 2, 2, 0.2)'
          }),
          stroke: new Stroke({
            color: 'rgba(255, 2, 2, 0.5)',
            lineDash: [10, 10],
            width: 2
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(255, 2, 2, 0.7)'
            }),
            fill: new Fill({
              color: 'rgba(255, 2, 2, 0.2)'
            })
          })
        })
      })
      this.map.addInteraction(this.draw)
      const self = this
      this.draw.on('drawstart', function (evt) {
        // Set sketch
        sketch = evt.feature
        source.clear()
        let listener = sketch.getGeometry().on('change', function (evt) {
          var geom = evt.target
          var output
          if (geom instanceof Polygon) {
            output = self.formatArea(geom) // REFACTOR SUGGESTION -- formatArea is not a fn...?
          } else if (geom instanceof LineString) {
            output = self.formatLength(geom)
          }

          // output[0] is m/km, output[1] is ft/mi
          self.measurement = output[0].split(' ')
          self.$emit('measurement', self.measurement)
        })
      })

      this.draw.on('drawend', function () {
        sketch = null
      })
    },
    formatLength(line) {
      const coordinates = line.getCoordinates();
      let totalLength = 0;

      for (let i = 0; i < coordinates.length - 1; i++) {
        const c1 = transform(coordinates[i], 'EPSG:3857', 'EPSG:4326');
        const c2 = transform(coordinates[i + 1], 'EPSG:3857', 'EPSG:4326');
        totalLength += getDistance(c1, c2);
      }

      var length = getLength(line)
      var output
      var meters
      var feet
      if (length > 100) {
        meters = `${Math.round((length / 1000) * 100) / 100} km`
        feet = `${Math.round((length / 1609.344) * 100) / 100} miles`
      } else {
        meters = `${Math.round(length * 100) / 100} m`
        feet = `${Math.round(length * 3.281 * 100) / 100} feet`
      }
      output = [meters, feet]
      return output
    },
    formatArea(polygon) {
      const area = getArea(polygon) //distance between the points (in meters).
      let output
      if (area > 10000) {
        output = Math.round((area / 1000) * 100) / 100
      } else {
        output = Math.round((area / 1000) * 100) / 100
      }
      return output * 0.621371 // convert to miles
    },
    setLidarIcon() {
      /* If user is zoomed in far enough to use lidar, use
      layer-group icon instead of exclamation-triangle icon */
      this.faIcon = this.lidarMinZoomAchieved ? 'layer-group' : 'exclamation-triangle'
      this.setLidarIconClass()
    },
    setLidarIconClass() {
      if (this.faIcon == 'exclamation-triangle') {
        this.faIconClass = 'collapsed-yellow'
      } else {
        if (this.showLidarPanel) {
          this.faIconClass = 'collapsed-blue'
        } else {
          this.faIconClass = 'collapsed-gray'
        }
      }
    },
    reset() {
      this.extentsPanelOpen = false
      if (this.measuringToolOn) {
        this.toggleMeasure();
      }
      if (this.lidarToggle) {
        // this.toggleLidar();
        this.lidarToggle = false;
      }
      this.baseMapsPanelOpen = false
      // this.measuringToolOn = false
      this.draw = null
      this.measurement = ['0', 'm']
      this.currentExtent = null
      // this.lidarToggle = false
      this.faIcon = 'exclamation-triangle'
    }
  },
  watch: {
    lidarMinZoomAchieved: function (newValue, oldValue) {
      this.setLidarIcon()
    },
    showLidarPanel: function (newValue, oldValue) {
      this.setLidarIcon()
    },
    selectedRelease: function (newValue) {
      this.reset()
    }
  }
}
</script>